/** Selected-structure Evidence & Conditions. Factual layers only — not a
 * conclusion, score, or instruction.
 */
import { useState } from "react";
import {
  BASKET, FIELD, HELPER, RESEARCH, REASON, dataLayerOf, eventLayerOf, actionLayerOf,
} from "@/workspace/screenLanguage";
import { technicalEntryContext, analystConsensus, POLICY_WINDOW } from "@/workspace/mockTickerData";
import { EarningsQualityBadge, KeyRow, SectionTitle, useIsNarrow } from "@/workspace/primitives";
import { StatusBadge } from "@/components/wheel/StatusBadge";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";
import { RESEARCH_FOCUS_KIND } from "@/workspace/live/workspaceLiveTypes";

const MARK_COLOR = {
  "●": "var(--hoip-text-secondary)",
  "▲": "#D97706",
  "!": "#F87171",
  "≈": "var(--hoip-text-secondary)",
  "○": "var(--hoip-text-tertiary)",
};

function Mark({ mark }) {
  return (
    <span aria-hidden="true" className="font-mono-data w-3 shrink-0" style={{ color: MARK_COLOR[mark] || "var(--hoip-text-secondary)" }}>
      {mark}
    </span>
  );
}

function Line({ mark, children }) {
  return (
    <div className="flex items-start gap-1.5 text-[11px] py-0.5" style={{ color: "var(--hoip-text-secondary)" }}>
      <Mark mark={mark} />
      <span>{children}</span>
    </div>
  );
}

function CompactRow({ title, mark, text, open, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-start gap-2 w-full text-left py-1.5 border-t"
      style={{ borderColor: "var(--hoip-divider)" }}
      data-testid={testid}
      aria-expanded={open}
    >
      <Mark mark={mark} />
      <span className="min-w-0 flex-1">
        <span className="text-[9px] uppercase tracking-wide block" style={{ color: "var(--hoip-text-tertiary)" }}>{title}</span>
        <span className="text-[11px]">{text}</span>
      </span>
      <span className="text-[10px] shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>{open ? "Hide" : "Show"}</span>
    </button>
  );
}

function fieldTerm(basis, kind) {
  if (kind === "quote") {
    if (basis === "observed") return FIELD.observed;
    if (basis === "partial") return FIELD.incomplete;
    if (basis === "unverified" || basis === "unavailable") return FIELD.unavailable;
    return FIELD.indicative;
  }
  if (kind === "ts") {
    if (basis === "unverified") return FIELD.unavailable;
    if (basis === "observed" || basis === "partial") return FIELD.observed;
    return FIELD.incomplete;
  }
  return FIELD.notInOutput;
}

function LiveConditions({ researchFocus, currentQuote }) {
  const narrow = useIsNarrow();
  const [open, setOpen] = useState({ historical: !narrow, quote: !narrow, action: !narrow });
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));
  const kind = researchFocus?.kind;
  const histText = kind === RESEARCH_FOCUS_KIND.ready
    ? LIVE_UNAVAILABLE.railHistoricalAvailable
    : kind === RESEARCH_FOCUS_KIND.limited
      ? LIVE_UNAVAILABLE.railHistoricalLimited
      : kind === RESEARCH_FOCUS_KIND.unavailable
        ? LIVE_UNAVAILABLE.railHistoricalUnavailable
        : kind === RESEARCH_FOCUS_KIND.pending
          ? "Loading historical strategy focus."
          : LIVE_UNAVAILABLE.railHistoricalChoose;
  const histMark = kind === RESEARCH_FOCUS_KIND.ready ? "●" : "○";
  const quoteBadge = currentQuote?.availability === "unverified_timestamp"
    ? LIVE_UNAVAILABLE.currentBadgeUnverified
    : LIVE_UNAVAILABLE.currentBadgeUnavailable;

  return (
    <section
      className="border p-3 min-w-0 mt-3"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}
      data-testid="evidence-conditions"
    >
      <div className="text-[11px] font-bold uppercase tracking-wide" style={{ fontFamily: "Chivo, sans-serif" }}>
        Evidence & Conditions
      </div>
      <p className="text-[10px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
        Available historical context, quote status and product action. Not a conclusion.
      </p>

      <div className="mt-1">
        <div data-testid="options-rail-historical">
          <CompactRow
            title={LIVE_UNAVAILABLE.railHistoricalKicker}
            mark={histMark}
            text={histText}
            open={open.historical}
            onToggle={() => toggle("historical")}
            testid="conditions-research-toggle"
          />
          <div className={open.historical ? "pl-4 pb-2 space-y-0.5" : "hidden"} data-testid="conditions-research-detail">
            <span data-testid="options-historical-badge"><StatusBadge tier={kind === RESEARCH_FOCUS_KIND.limited ? "caution" : "info"} text={LIVE_UNAVAILABLE.historicalBadge} /></span>
            {researchFocus?.displayName ? <KeyRow label="Research focus">{researchFocus.displayName}</KeyRow> : null}
            <KeyRow label="Historical model">{LIVE_UNAVAILABLE.researchSimulationLabel}</KeyRow>
          </div>
        </div>

        <div data-testid="options-current-priced">
          <CompactRow
            title={LIVE_UNAVAILABLE.railQuoteKicker}
            mark="!"
            text={LIVE_UNAVAILABLE.railQuoteSummary}
            open={open.quote}
            onToggle={() => toggle("quote")}
            testid="conditions-data-toggle"
          />
          <div className={open.quote ? "pl-4 pb-2 space-y-0.5" : "hidden"} data-testid="conditions-data-detail">
            <span data-testid="options-current-badge"><StatusBadge tier="blocking" text={quoteBadge} /></span>
            <p className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-secondary)" }} data-testid="options-current-priced-copy">
              {currentQuote?.copy || LIVE_UNAVAILABLE.currentPricedUnverified}
            </p>
          </div>
        </div>

        <div data-testid="options-current-action">
          <CompactRow
            title={LIVE_UNAVAILABLE.railActionKicker}
            mark="!"
            text={LIVE_UNAVAILABLE.railActionSummary}
            open={open.action}
            onToggle={() => toggle("action")}
            testid="conditions-event-toggle"
          />
          <div className={open.action ? "pl-4 pb-2 space-y-0.5" : "hidden"}>
            <p className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-secondary)" }} data-testid="options-current-action-copy">
              {LIVE_UNAVAILABLE.currentActionUnavailable}. {LIVE_UNAVAILABLE.currentActionDetail}.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function EvidenceConditionsPanel({ ticker, row, researchFocus, currentQuote }) {
  const narrow = useIsNarrow();
  const [open, setOpen] = useState({ data: false, event: false, research: false });
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  if (ticker?.live) {
    return (
      <LiveConditions
        researchFocus={researchFocus || ticker.researchFocus}
        currentQuote={currentQuote || ticker.currentQuote}
      />
    );
  }

  if (!row || !ticker) return null;

  const ev = row.evidence;
  const data = dataLayerOf(ev.basis);
  const event = eventLayerOf(row.conflict);
  const action = actionLayerOf(data.key);
  const ctx = ticker.live ? null : technicalEntryContext(ticker.ticker);
  const analyst = ticker.live
    ? (ticker.fundamentalsLive?.targetMean != null || ticker.fundamentalsLive?.analystCount != null
      ? {
        median: ticker.fundamentalsLive.targetMean,
        count: ticker.fundamentalsLive.analystCount,
        low: null,
        high: null,
        asOf: null,
      }
      : null)
    : analystConsensus(ticker.ticker);
  const hist = ev.historical;
  const reg = ev.regime;
  const ready = ev.readiness;
  const strike = ev.legs?.[0]?.strike;
  const dist = analyst?.median != null && strike != null
    ? +(analyst.median - strike).toFixed(2)
    : null;

  const dataSummary = action.key === "unavailable" ? action.text : data.text;
  const eventSummary = event.text;
  const researchSummary = reg.insufficient
    ? RESEARCH.insufficient
    : (reg.regime_fit === "available" ? RESEARCH.available : RESEARCH.limited);

  const dataMark = action.key === "unavailable" ? "!" : data.mark;
  const eventMark = event.mark;
  const researchMark = reg.insufficient ? "○" : "≈";

  const stack = narrow ? "space-y-2" : "";

  return (
    <section
      className="border p-3 min-w-0 mt-3"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}
      data-testid="evidence-conditions"
    >
      <div className="text-[11px] font-bold uppercase tracking-wide" style={{ fontFamily: "Chivo, sans-serif" }}>
        Evidence & Conditions
      </div>
      <p className="text-[10px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>
        Available data, events and historical context for {row.strategy}. Not a conclusion.
      </p>

      <div className={stack || "mt-1"} data-testid="evidence-conditions-compact">
        <CompactRow
          title="Data & Structure"
          mark={dataMark}
          text={dataSummary}
          open={open.data}
          onToggle={() => toggle("data")}
          testid="conditions-data-toggle"
        />
        {open.data ? (
          <div className="pl-4 pb-2 space-y-0.5" data-testid="conditions-data-detail">
            <KeyRow label="Structure validity">{ready.structureValid ? FIELD.observed : FIELD.unavailable}</KeyRow>
            <KeyRow label="Width / distinct legs">{ev.width || FIELD.notInOutput}</KeyRow>
            <KeyRow label="Required bid/ask fields">{fieldTerm(ev.basis, "quote")}</KeyRow>
            <KeyRow label="Pricing method">{ready.pricingMethod}</KeyRow>
            <KeyRow label="Pricing-method completeness">{ready.quoteCompleteness}</KeyRow>
            <KeyRow label="Option quote timestamp">{fieldTerm(ev.basis, "ts")}</KeyRow>
            <KeyRow label="Last trade timestamp">{FIELD.notInOutput}</KeyRow>
            <KeyRow label="Liquidity fields">{FIELD.notConfigured}</KeyRow>
            <KeyRow label="Defined-risk cap">
              {hist.defined_risk_cap != null ? `$${hist.defined_risk_cap.toLocaleString()}` : FIELD.notInOutput}
            </KeyRow>
            <KeyRow label="Collateral / debit">
              {ev.weekly?.extraValue || ev.weekly?.value || FIELD.notInOutput}
            </KeyRow>
            <KeyRow label="Product action">{action.text}</KeyRow>
          </div>
        ) : null}

        <CompactRow
          title="Event & Parameters"
          mark={eventMark}
          text={eventSummary}
          open={open.event}
          onToggle={() => toggle("event")}
          testid="conditions-event-toggle"
        />
        {open.event ? (
          <div className="pl-4 pb-2 space-y-0.5" data-testid="conditions-event-detail">
            <KeyRow label="Selected DTE">{row.dte ?? "—"}</KeyRow>
            <KeyRow label="Active DTE policy">{POLICY_WINDOW.min}–{POLICY_WINDOW.max} DTE</KeyRow>
            <KeyRow label="Earnings state">{event.text}</KeyRow>
            <KeyRow label="Earnings-date quality">
              <span className="inline-flex items-center gap-1.5">
                {row.conflict?.quality || "unavailable"}
                {row.conflict?.quality ? <EarningsQualityBadge quality={row.conflict.quality} /> : null}
              </span>
            </KeyRow>
            <KeyRow label="Confirmed event in window">
              {row.conflict?.state === "inside" && row.conflict?.quality === "confirmed" ? FIELD.overlap : "No"}
            </KeyRow>
            <KeyRow label="Liquidity parameters">{FIELD.notConfigured}</KeyRow>
            <KeyRow label="Portfolio / risk parameters">{FIELD.notEvaluated}</KeyRow>
            <KeyRow label="Assignment / call-away">{hist.assignment_rate != null ? `${hist.assignment_rate.toFixed(1)}% historical · ${FIELD.informational}` : FIELD.informational}</KeyRow>
          </div>
        ) : null}

        <CompactRow
          title="Research Context"
          mark={researchMark}
          text={researchSummary}
          open={open.research}
          onToggle={() => toggle("research")}
          testid="conditions-research-toggle"
        />
        {open.research ? (
          <div className="pl-4 pb-2 space-y-1" data-testid="conditions-research-detail">
            <KeyRow label="Confirmed HMM regime">{reg.confirmedState}</KeyRow>
            <KeyRow label="Instant regime">{ticker.regime?.instantState || FIELD.notInOutput}</KeyRow>
            <KeyRow label="Regime confidence">{Math.round((reg.confidence || 0) * 100)}%</KeyRow>
            <KeyRow label="Regime sample count">{reg.sample_size ?? RESEARCH.unavailable}</KeyRow>
            <KeyRow label="Historical observations">
              {reg.insufficient
                ? `${RESEARCH.insufficient} · n=${reg.sample_size ?? "—"}`
                : `n=${reg.sample_size}`}
            </KeyRow>
            {!reg.insufficient ? (
              <>
                <KeyRow label="Historical win rate">{reg.win_rate == null ? RESEARCH.unavailable : `${reg.win_rate.toFixed(1)}%`}</KeyRow>
                <KeyRow label="Historical RoR">{reg.avg_return_on_risk == null ? RESEARCH.unavailable : `${(reg.avg_return_on_risk * 100).toFixed(1)}%`}</KeyRow>
              </>
            ) : null}
            <KeyRow label="Daily EMA 9/20">{ctx?.daily?.line || RESEARCH.unavailable}</KeyRow>
            <KeyRow label="Weekly EMA 10/20">{ctx?.weekly?.line || RESEARCH.unavailable}</KeyRow>
            <KeyRow label="Bollinger location">{ctx?.meanRev?.bbPctLabel || RESEARCH.unavailable}</KeyRow>
            <KeyRow label="RSI">{ctx?.meanRev?.rsi ?? RESEARCH.unavailable}</KeyRow>
            <KeyRow label="Relative strength">{ctx ? `${ctx.relative.spyLabel} · ${ctx.relative.sectorLabel}` : RESEARCH.unavailable}</KeyRow>
            <KeyRow label="IV / premium">{ticker.premium?.label || RESEARCH.unavailable}</KeyRow>
            <KeyRow label="Fundamental data">{ticker.fundamentalScore != null ? RESEARCH.available : RESEARCH.unavailable}</KeyRow>
            <KeyRow label="Analyst consensus">{analyst ? RESEARCH.reference : RESEARCH.unavailable}</KeyRow>
            {analyst ? (
              <div className="pt-1 space-y-0.5" data-testid="analyst-consensus-reference">
                <KeyRow label="Median target">{analyst.median == null ? RESEARCH.unavailable : `$${analyst.median}`}</KeyRow>
                <KeyRow label="Coverage count">{analyst.count ?? RESEARCH.unavailable}</KeyRow>
                <KeyRow label="Target range">
                  {analyst.low == null || analyst.high == null ? RESEARCH.unavailable : `$${analyst.low} – $${analyst.high}`}
                </KeyRow>
                <KeyRow label="As-of">{analyst.asOf || RESEARCH.unavailable}</KeyRow>
                <KeyRow label="Selected strike reference">{strike != null ? strike : RESEARCH.unavailable}</KeyRow>
                <KeyRow label="Distance to median">
                  {dist == null ? RESEARCH.unavailable : `${dist >= 0 ? "+" : "−"}$${Math.abs(dist).toFixed(2)}`}
                </KeyRow>
                <p className="text-[10px] pt-1" style={{ color: "var(--hoip-text-tertiary)" }}>{HELPER.analyst}</p>
              </div>
            ) : null}
            <KeyRow label="Ticker Talk / ownership">{RESEARCH.reference}</KeyRow>
          </div>
        ) : null}
      </div>

      {(open.data || open.event || open.research) ? (
        <p className="text-[10px] leading-snug mt-2 pt-2 border-t" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>
          {HELPER.conditions}
        </p>
      ) : null}
    </section>
  );
}

export function ConditionsExampleMarks({ ticker, row }) {
  if (!row) return null;
  const data = dataLayerOf(row.evidence.basis);
  const event = eventLayerOf(row.conflict);
  const action = actionLayerOf(data.key);
  return (
    <div className="text-[10px] space-y-0.5" data-testid="conditions-example-marks">
      <Line mark={data.mark}>{data.text}</Line>
      {action.key === "unavailable" ? <Line mark="!">{BASKET.unavailable}</Line> : null}
      {data.key === "indicative" ? <Line mark="≈">{REASON.indicative}</Line> : null}
      <Line mark={event.mark}>{event.text}</Line>
    </div>
  );
}
